/**
 * ClientCasePicker — lets a signed-in client choose among multiple granted cases.
 *
 * Dark theme, black + orange tokens only.
 */

import React from 'react';
import { Briefcase, ChevronRight } from 'lucide-react';

export interface ClientCasePickerCase {
  report_id: string;
  client_name: string;
}

interface ClientCasePickerProps {
  cases: ClientCasePickerCase[];
  onSelect: (report_id: string) => void;
  error?: string | null;
  pendingReportId?: string | null;
}

export default function ClientCasePicker({ cases, onSelect, error, pendingReportId }: ClientCasePickerProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2
          className="font-heading text-2xl font-bold text-[var(--color-text-primary)] mb-2"
        >
          Select a Case
        </h2>
        <p className="text-[var(--color-text-secondary)]">
          You have access to multiple cases. Please select one to continue.
        </p>
      </div>

      {error && (
        <p className="text-sm text-[var(--color-needs-human-text)] bg-[var(--color-needs-human-tint)] border border-[var(--color-needs-human-tint-bd)] rounded-lg px-4 py-3">
          {error}
        </p>
      )}

      <div className="space-y-3">
        {cases.map((c) => {
          const isPending = pendingReportId === c.report_id;
          return (
            <button
              key={c.report_id}
              onClick={() => onSelect(c.report_id)}
              disabled={Boolean(pendingReportId)}
              data-testid={`case-picker-${c.report_id}`}
              className="w-full flex items-center gap-4 p-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] hover:border-[var(--color-brand)] transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="w-10 h-10 rounded-lg bg-[var(--color-bg-elevated)] flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-5 h-5 text-[var(--color-brand)]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[var(--color-text-primary)] truncate">
                  {c.client_name}
                </p>
                <p className="text-sm text-[var(--color-text-muted)]">
                  Case ID: {c.report_id.slice(0, 8)}…
                </p>
              </div>
              {isPending ? (
                <span className="text-sm text-[var(--color-text-muted)]">Opening…</span>
              ) : (
                <ChevronRight className="w-5 h-5 text-[var(--color-text-muted)] flex-shrink-0" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
